import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import EmissionFactorSearch from '../components/EmissionFactorSearch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Repeat } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_UNIT = { unitCategory: '', unit: '' };
const EMPTY_DETAILS = { value: '', source_name: '', source_url: '', additional_information: '' };
const EMPTY_CATEGORY = { conversion_sub_category: '', conversion_type: '', conversion_sub_type: '' };

// One category field: a dropdown with the emission factor's own value, or "*" to apply the conversion to every value.
// A locked field (sub category) is shown as plain read-only text: it always follows the emission factor.
// A previously saved value that is neither (from an older free-text entry) is kept as an extra option.
const CategoryField = ({ label, allLabel, locked, value, efValue, onChange, testId }) => {
  const options = [
    ...(efValue ? [{ value: efValue, text: efValue }] : []),
    ...(value && value !== '*' && value !== efValue ? [{ value, text: `${value} (saved)` }] : []),
    { value: '*', text: allLabel },
  ];
  if (locked) {
    return (
      <div className="space-y-2">
        <Label>{label} <span className="text-red-500">*</span></Label>
        <Input value={value} disabled data-testid={`${testId}-input`} />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Label>{label} <span className="text-red-500">*</span></Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`${testId}-select`}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.text}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Unit category first, then a unit dropdown limited to that category. excludeCategory hides a category chosen elsewhere.
const UnitPicker = ({ label, units, value, onChange, testId, excludeCategory }) => {
  const unitCategories = useMemo(
    () => [...new Set(units.map(u => u.unit_category))].filter(c => c !== excludeCategory).sort(),
    [units, excludeCategory]
  );
  const unitsInCategory = units.filter(u => u.unit_category === value.unitCategory).map(u => u.unit);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>{label} Unit Category <span className="text-red-500">*</span></Label>
        <Select value={value.unitCategory} onValueChange={(unitCategory) => onChange({ unitCategory, unit: '' })}>
          <SelectTrigger data-testid={`${testId}-unit-category`}>
            <SelectValue placeholder="Select unit category" />
          </SelectTrigger>
          <SelectContent>
            {unitCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{label} Unit <span className="text-red-500">*</span></Label>
        <Select value={value.unit} onValueChange={(unit) => onChange({ ...value, unit })} disabled={!value.unitCategory}>
          <SelectTrigger data-testid={`${testId}-unit`}>
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            {unitsInCategory.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// Creates a category conversion, or edits and resubmits one when the route has an :id.
export default function CategoryConversionFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [units, setUnits] = useState([]);
  const [existing, setExisting] = useState(null);
  const [selectedEF, setSelectedEF] = useState(null);
  const [category, setCategory] = useState(EMPTY_CATEGORY);
  const [from, setFrom] = useState(EMPTY_UNIT);
  const [to, setTo] = useState(EMPTY_UNIT);
  const [details, setDetails] = useState(EMPTY_DETAILS);

  useEffect(() => {
    axios.get(`${API_URL}/category-conversions/units`)
      .then(res => setUnits(res.data))
      .catch(() => toast.error('Failed to load units'));
  }, []);

  // Edit mode: load the saved submission and its emission factor
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API_URL}/category-conversions/${id}`)
      .then(async ({ data }) => {
        setCategory({
          conversion_sub_category: data.conversion_sub_category,
          conversion_type: data.conversion_type,
          conversion_sub_type: data.conversion_sub_type,
        });
        setDetails({
          value: String(data.value),
          source_name: data.source_name,
          source_url: data.source_url,
          additional_information: data.additional_information,
        });
        setExisting(data);
        setSelectedEF((await axios.get(`${API_URL}/emission-factors/${data.emission_factor_id}`)).data);
      })
      .catch(() => toast.error('Failed to load category conversion'));
  }, [id, isEdit]);

  // Edit mode: work out each saved unit's category once both the units and the submission are loaded
  useEffect(() => {
    if (!existing || units.length === 0) return;
    const toUnitPicker = (unit) => ({ unit, unitCategory: units.find(u => u.unit === unit)?.unit_category || '' });
    setFrom(toUnitPicker(existing.conversion_from));
    setTo(toUnitPicker(existing.conversion_to));
  }, [existing, units]);

  const handleSelectEF = (ef) => {
    setSelectedEF(ef);
    setCategory({
      conversion_sub_category: ef.sub_category || '*',
      conversion_type: ef.type || '*',
      conversion_sub_type: ef.sub_type || '*',
    });
  };

  // The To category can't be the same as From, so clear To if From now matches it
  const handleFromChange = (next) => {
    setFrom(next);
    if (next.unitCategory && next.unitCategory === to.unitCategory) setTo(EMPTY_UNIT);
  };

  const setCategoryField = (field) => (value) => setCategory(prev => ({ ...prev, [field]: value }));
  const setDetail = (field) => (e) => setDetails(prev => ({ ...prev, [field]: e.target.value }));

  const allFieldsFilled =
    selectedEF && from.unit && to.unit &&
    [...Object.values(category), ...Object.values(details)].every(v => v.trim());

  const handleSubmit = async () => {
    const payload = {
      emission_factor_id: selectedEF.emission_factor_id,
      ...category,
      conversion_from: from.unit,
      conversion_to: to.unit,
      ...details,
      value: Number(details.value),
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/category-conversions/${id}`, payload);
        toast.success('Category conversion resubmitted for approval');
        navigate(`/category-conversions/${id}`);
      } else {
        await axios.post(`${API_URL}/category-conversions`, payload);
        toast.success('Category conversion submitted for approval');
        navigate('/category-conversions');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((Array.isArray(detail) ? detail[0].msg : detail) || 'Failed to save category conversion');
    } finally {
      setSubmitting(false);
    }
  };

  const backUrl = isEdit ? `/category-conversions/${id}` : '/category-conversions';

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="category-conversion-form-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              {isEdit ? `Edit Category Conversion #${id}` : 'Create Category Conversion'}
            </h1>
            <p className="text-muted-foreground mt-1">Convert between units for an emission factor's category. All fields are mandatory.</p>
          </div>
        </div>

        <EmissionFactorSearch selectedEF={selectedEF} onSelect={handleSelectEF} />

        {selectedEF && (
          <>
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Repeat className="w-5 h-5" />Conversion Category</CardTitle>
                <CardDescription>Use the emission factor's value, or choose All (*) to apply the conversion to every value.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <CategoryField label="Conversion Sub Category" locked value={category.conversion_sub_category} efValue={selectedEF.sub_category} onChange={setCategoryField('conversion_sub_category')} testId="sub-category" />
                  <CategoryField label="Conversion Type" allLabel="All types (*)" value={category.conversion_type} efValue={selectedEF.type} onChange={setCategoryField('conversion_type')} testId="type" />
                  <CategoryField label="Conversion Sub Type" allLabel="All sub types (*)" value={category.conversion_sub_type} efValue={selectedEF.sub_type} onChange={setCategoryField('conversion_sub_type')} testId="sub-type" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Conversion Units</CardTitle>
                <CardDescription>Pick the unit category first, then the unit. The To category must differ from the From category.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <UnitPicker label="From" units={units} value={from} onChange={handleFromChange} testId="from" />
                <UnitPicker label="To" units={units} value={to} onChange={setTo} testId="to" excludeCategory={from.unitCategory} />
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Conversion Details</CardTitle>
                <CardDescription>1 {from.unit || '[from unit]'} = value × {to.unit || '[to unit]'}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="value">Value <span className="text-red-500">*</span></Label>
                    <Input id="value" type="number" step="any" min="0" value={details.value} onChange={setDetail('value')} data-testid="value-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source_name">Source Name <span className="text-red-500">*</span></Label>
                    <Input id="source_name" value={details.source_name} onChange={setDetail('source_name')} data-testid="source-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source_url">Source URL <span className="text-red-500">*</span></Label>
                    <Input id="source_url" value={details.source_url} onChange={setDetail('source_url')} data-testid="source-url-input" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="additional_information">Additional Information <span className="text-red-500">*</span></Label>
                    <Textarea id="additional_information" rows={3} value={details.additional_information} onChange={setDetail('additional_information')} data-testid="additional-information-input" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(backUrl)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !allFieldsFilled} className="btn-primary" data-testid="submit-conversion-btn">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Resubmit for Approval' : 'Submit for Approval'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
